import {Button, CircularProgress, Grid} from '@material-ui/core';
import PhoneIcon from '@material-ui/icons/Phone';
import SearchIcon from '@material-ui/icons/Search';
import {useStyles} from './LiveCallComponents.styles';
import {
    LiveCallComponentsProps,
    CallComponentProps,
    BasicStatusIndicators,
    StatusIndicatorsSection,
    ClientInfoSection,
} from './LiveCallShared';
import AgentAssistPanel from '@/Agent/Assist/AgentAssistPanel';
import RootStore from '@/Core/mobx/RootStore';
import {PERSONA_TOOLTIP_MAP} from './utils/personaTooltipMap';

export const LoadingComponent = ({
    connectionStatus,
    subscriptionStatus,
    showRefreshButton,
    handleRefresh,
}: Pick<
    LiveCallComponentsProps,
    'connectionStatus' | 'subscriptionStatus' | 'showRefreshButton' | 'handleRefresh'
>) => {
    const classes = useStyles();

    return (
        <div className={classes.container}>
            <BasicStatusIndicators
                connectionStatus={connectionStatus}
                subscriptionStatus={subscriptionStatus}
                showRefreshButton={showRefreshButton}
                handleRefresh={handleRefresh}
            />
            <div className={classes.contentCard}>
                <div className={classes.loadingContainer}>
                    <CircularProgress size={48} className={classes.loadingSpinner} />
                    <div className={classes.loadingText}>Loading Call Information</div>
                    <div className={classes.loadingSubtext}>Please wait while we check for active calls...</div>
                </div>
            </div>
        </div>
    );
};

export const NoCallRecordFound = ({
    connectionStatus,
    subscriptionStatus,
    showRefreshButton,
    handleRefresh,
}: Pick<
    LiveCallComponentsProps,
    'connectionStatus' | 'subscriptionStatus' | 'showRefreshButton' | 'handleRefresh'
>) => {
    const classes = useStyles();

    return (
        <div className={classes.container}>
            <BasicStatusIndicators
                connectionStatus={connectionStatus}
                subscriptionStatus={subscriptionStatus}
                showRefreshButton={showRefreshButton}
                handleRefresh={handleRefresh}
            />
            <div className={classes.contentCard}>
                <div className={classes.waitingContainer}>
                    <div className={classes.waitingIconContainer}>
                        <div className={`${classes.phoneIconContainer} ${classes.pulseAnimation}`}>
                            <PhoneIcon className={classes.phoneIcon} />
                        </div>
                    </div>
                    <div className={classes.waitingTitle}>Waiting for Calls</div>
                    <div className={classes.waitingDescription}>
                        New calls will appear here automatically when they come in.
                    </div>
                </div>
            </div>
        </div>
    );
};

export const NoClientRecordFound = ({
    currentCall,
    onHubspot,
    connectionStatus,
    subscriptionStatus,
    showRefreshButton,
    handleRefresh,
}: Pick<
    LiveCallComponentsProps,
    'currentCall' | 'onHubspot' | 'connectionStatus' | 'subscriptionStatus' | 'showRefreshButton' | 'handleRefresh'
>) => {
    const classes = useStyles();

    return (
        <div className={classes.container}>
            <BasicStatusIndicators
                connectionStatus={connectionStatus}
                subscriptionStatus={subscriptionStatus}
                showRefreshButton={showRefreshButton}
                handleRefresh={handleRefresh}
            />
            <div className={classes.noCallContainer}>
                <div className={classes.noCallTitle}>{currentCall?.clientEmail}</div>
                <div className={classes.noCallText}>
                    Hmm. We didn't find any record for this client <SearchIcon />
                </div>
                <div className={classes.noCallText}>Jump over to Hubspot to find the client.</div>
                <Button className={classes.hubspotButton} onClick={onHubspot}>
                    Go To Hubspot
                </Button>
            </div>
        </div>
    );
};

export const DialingCall = ({
    currentCall,
    onNewForm,
    getUserData,
    connectionStatus,
    subscriptionStatus,
    showRefreshButton,
    handleRefresh,
    onClearCall,
    tooltipText,
}: CallComponentProps) => {
    const classes = useStyles();
    const {assistStore} = RootStore;

    return (
        <div className={classes.container}>
            <StatusIndicatorsSection
                connectionStatus={connectionStatus}
                subscriptionStatus={subscriptionStatus}
                showRefreshButton={showRefreshButton}
                handleRefresh={handleRefresh}
                onClearCall={onClearCall}
            />

            <div className={`${classes.callStatusBanner} ${classes.dialingBanner}`}>
                <CircularProgress size={16} thickness={5} style={{color: 'white'}} />
                <span>Dialing Client...</span>
            </div>

            <Grid container spacing={1}>
                <Grid item xs={8}>
                    <ClientInfoSection
                        currentCall={currentCall}
                        getUserData={getUserData}
                        onNewForm={onNewForm}
                        variant="dialing"
                        tooltipText={tooltipText}
                    />
                </Grid>
                <Grid item xs={4}>
                    <AgentAssistPanel onValueChangedNotes={assistStore.setNotes} isLiveCall={true} />
                </Grid>
            </Grid>
        </div>
    );
};

export const ConnectedCall = ({
    currentCall,
    onNewForm,
    getUserData,
    connectionStatus,
    subscriptionStatus,
    showRefreshButton,
    handleRefresh,
    onClearCall,
    tooltipText,
}: CallComponentProps) => {
    const classes = useStyles();
    const {assistStore} = RootStore;

    return (
        <div className={classes.container}>
            <StatusIndicatorsSection
                connectionStatus={connectionStatus}
                subscriptionStatus={subscriptionStatus}
                showRefreshButton={showRefreshButton}
                handleRefresh={handleRefresh}
                onClearCall={onClearCall}
            />

            <div className={`${classes.callStatusBanner} ${classes.connectedBanner}`}>
                <span>Call Connected</span>
            </div>

            <Grid container spacing={1}>
                <Grid item xs={8}>
                    <ClientInfoSection
                        currentCall={currentCall}
                        getUserData={getUserData}
                        onNewForm={onNewForm}
                        variant="connected"
                        tooltipText={tooltipText}
                    />
                </Grid>
                <Grid item xs={4}>
                    <AgentAssistPanel onValueChangedNotes={assistStore.setNotes} isLiveCall={true} />
                </Grid>
            </Grid>
        </div>
    );
};
